
/**
 * Module dependencies.
 */

import { Box } from 'react-components/layout';
import { Button, Divider, Heading, Link, LocalImageSources, Text, logo, typography } from 'slyk-web-libs/design-system';
import { Container } from './container';
import { Icon } from 'react-components/media';
import { Trans } from 'react-i18next';
import { color, media } from 'react-components/styles';
import { dashboardUrlResolver } from 'client/core/utils/url-resolver';
import { getImageSources } from 'client/core/utils/image';
import { useTranslate } from 'slyk-web-libs/i18n';
import RawHtml from 'react-components/raw-html';
import React from 'react';
import config from 'config';
import routes from 'slyk-web-libs/config/dashboard-routes';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  getSlykApp: () => void;
  payspaceName: string;
  payspaceSlug: string;
};

/**
 * Links configuration constant.
 */

const linksConfig: Array<{ label: string; url: string }> = [{
  label: 'payspaceTermsOfService',
  url: config.get('externalLinks.userTerms')
}, {
  label: 'endUserTermsOfService',
  url: config.get('externalLinks.payspaceTerms')
}, {
  label: 'privacyPolicy',
  url: config.get('externalLinks.privacy')
}];

/**
 * `FooterWrapper` styled component.
 */

const FooterWrapper = styled.footer`
  background-color: ${color('secondary')};
  color: ${color('white')};
  position: relative;
`;

/**
 * `FooterImage` styled component.
 */

const FooterImage = styled.img`
  bottom: 0;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
`;

/**
 * `StyledDivider` styled component.
 */

const StyledDivider = styled(Divider)`
  background-color: ${color.transparentize('white', 0.1)};
  position: relative;
`;

/**
 * `Title` styled component.
 */

const Title = styled(Heading).attrs({ as: 'p', level: 'h4' })`
  grid-area: title;
  margin-bottom: 4px;

  ${media.min('ms')`
    margin-bottom: 16px;
  `}
`;

/**
 * `Lead` styled component.
 */

const Lead = styled(Text)`
  grid-area: lead;
  margin-bottom: 48px;

  ${media.min('ms')`
    margin-bottom: 70px;
  `}
`;

/**
 * `Author` styled component.
 */

const Author = styled(Text)`
  grid-area: author;
`;

/**
 * `Name` styled component.
 */

const Name = styled(Heading)`
  margin-bottom: 32px;
  padding-top: 80px;
  text-align: center;

  ${media.min('ms')`
    margin-bottom: 45px;
  `}
`;

/**
 * `StyledButton` styled component.
 */

const StyledButton = styled(Button)`
  width: 100%;

  ${media.min('xs')`
    margin: 0 auto;
    width: max-content;
  `}
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  column-gap: 32px;
  display: grid;
  grid-template-areas: 'title' 'lead' 'links' 'owner' 'author';
  grid-template-columns: 1fr;
  padding: 45px 0 78px;
  text-align: left;

  ${media.min('ms')`
    grid-template-areas:
      'title .'
      'lead links'
      '. .'
      'author owner';
    grid-template-columns: 1fr 1fr;
    padding: 52px 0 34px;
  `}

  ${media.min('xl')`
    margin: 0 -32px;
  `}
`;

/**
 * `List` styled component.
 */

const List = styled.ul`
  grid-area: links;
  margin-bottom: 24px;

  ${media.min('ms')`
    text-align: right;
  `}
`;

/**
 * `StyledLink` styled component.
 */

const StyledLink = styled(Link)`
  ${typography.styles.textNormal}

  color: ${color('white')};
`;

/**
 * `AuthorLink` styled component.
 */

const AuthorLink = styled(Link)`
  ${typography.styles.textNormal}

  color: ${color('white')};
  margin-left: 6px;
`;

/**
 * `OwnerWrapper` styled component.
 */

const OwnerWrapper = styled(Text)`
  grid-area: owner;
  margin-bottom: 48px;

  ${media.min('ms')`
    margin-bottom: 0;
    text-align: right;
  `}
`;

/**
 * Export `Footer` component.
 */

export function Footer({ getSlykApp, payspaceName, payspaceSlug }: Props): JSX.Element {
  const { translate } = useTranslate();
  const footerImage = getImageSources('home/footer-bg', 'webp') as LocalImageSources;

  return (
    <FooterWrapper>
      <FooterImage
        alt={''}
        height={'100%'}
        loading={'lazy'}
        src={footerImage.defaultUrl}
        width={'100%'}
      />

      <Container>
        <Box
          marginBottom={'56px'}
          marginBottomXl={'70px'}
          marginLeft={'auto'}
          marginRight={'auto'}
          maxWidth={'580px'}
        >
          <RawHtml element={Name}>
            {translate('footer.appDownload.title', { name: payspaceName })}
          </RawHtml>

          <StyledButton
            onClick={getSlykApp}
            size={'large'}
          >
            {translate('footer.appDownload.action')}
          </StyledButton>
        </Box>
      </Container>

      <Box as={'footer'}>
        <StyledDivider />

        <Container>
          <Grid>
            <Title>
              {translate('footer.title')}
            </Title>

            <Lead>
              {translate('footer.lead')}
            </Lead>

            <Author>
              {translate('footer.author')}

              <AuthorLink
                aria-label={'Slyk website'}
                href={config.get('externalLinks.slykWebsite')}
                rel={'noopener'}
                target={'_blank'}
              >
                <Icon
                  aria-hidden
                  icon={logo}
                  size={'56px'}
                />
              </AuthorLink>
            </Author>

            <List>
              {linksConfig.map(({ label, url }) => (
                <li key={label}>
                  <StyledLink
                    href={url}
                    rel={'noopener'}
                    target={'_blank'}
                  >
                    {translate(`footer.links.${label}`)}
                  </StyledLink>
                </li>
              ))}
            </List>

            <OwnerWrapper>
              <Trans
                components={[
                  <StyledLink
                    href={dashboardUrlResolver(payspaceSlug, routes.signIn)}
                    key={0}
                  />
                ]}
                i18nKey={'footer.owner'}
              />
            </OwnerWrapper>
          </Grid>
        </Container>
      </Box>
    </FooterWrapper>
  );
}
